import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Flame, Zap, Shield, Star, TrendingUp } from 'lucide-react';
import { DoodleSquiggle } from './Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MULTIPLIER_TIERS = [
  { days: 0, mult: '1x', color: '#D0D0D0', label: 'No streak' },
  { days: 3, mult: '1.5x', color: '#B9FBC0', label: '3-day streak' },
  { days: 7, mult: '2x', color: '#A2D2FF', label: '7-day streak' },
  { days: 14, mult: '2.5x', color: '#FFC8DD', label: '14-day streak' },
  { days: 30, mult: '3x', color: '#CDB4DB', label: '30-day streak' },
];

export default function StreakBanner() {
  const [streak, setStreak] = useState(null);

  const fetchStreak = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/streak`, { withCredentials: true });
      setStreak(data);
    } catch (e) {
      console.error('Failed to fetch streak:', e);
    }
  }, []);

  useEffect(() => { fetchStreak(); }, [fetchStreak]);

  if (!streak) return null;

  const currentTier = MULTIPLIER_TIERS.filter(t => streak.streak >= t.days).pop() || MULTIPLIER_TIERS[0];

  return (
    <div className="neo-card p-4 mb-6 flex flex-col sm:flex-row items-center gap-4" data-testid="streak-banner" style={{ backgroundColor: currentTier.color + '30' }}>
      {/* Streak fire */}
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-2xl border-2 border-black flex items-center justify-center" style={{ backgroundColor: currentTier.color }}>
          <Flame className="w-7 h-7" />
        </div>
        <div>
          <p className="text-2xl font-bold font-['Fredoka'] leading-none">{streak.streak} day{streak.streak !== 1 ? 's' : ''}</p>
          <p className="text-sm text-gray-600">Daily streak</p>
        </div>
      </div>

      {/* Multiplier */}
      <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border-2 border-black">
        <Zap className="w-4 h-4 text-yellow-500" />
        <span className="font-bold font-['Fredoka'] text-lg">{streak.multiplier}x</span>
        <span className="text-xs text-gray-500">points</span>
      </div>

      {/* Free miss */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border-2 border-black">
        <Shield className="w-4 h-4 text-blue-500" />
        <span className="text-xs font-bold">{streak.free_misses_left} free miss left</span>
      </div>

      {/* Next milestone */}
      {streak.next_threshold && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <TrendingUp className="w-4 h-4" />
          <span>
            {streak.next_threshold - streak.streak} more day{streak.next_threshold - streak.streak !== 1 ? 's' : ''} to {streak.next_multiplier}x
          </span>
        </div>
      )}

      {/* Tier dots */}
      <div className="hidden md:flex items-center gap-1 ml-auto">
        {MULTIPLIER_TIERS.map((t, i) => (
          <div
            key={i}
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[8px] font-bold ${
              streak.streak >= t.days ? 'border-black' : 'border-gray-300'
            }`}
            style={{ backgroundColor: streak.streak >= t.days ? t.color : '#f0f0f0' }}
            title={t.label}
          >
            {streak.streak >= t.days && <Star className="w-3 h-3" fill="currentColor" />}
          </div>
        ))}
      </div>
    </div>
  );
}
